import { useCallback, useEffect, useRef, useState } from "react";

import { annotationStudioText, resolveAnnotationOperationErrorMessage } from "./messages";
import type {
  AnnotationSetValidationState,
  AnnotationStudioController,
  AnnotationStudioExitRequestResult,
  AnnotationStudioExitResult,
  AnnotationStudioLocale,
  AnnotationStudioValidationReviewResult,
} from "./types";

export type StudioExitActionState = "idle" | "saving" | "checking" | "leaving";

interface PendingStreamChange {
  readonly streamId: string;
  readonly dirtyFrameIds: readonly string[];
  readonly afterChange?: () => void;
}

interface PendingValidationReviewRequest {
  readonly requestId: number;
  readonly serial: number;
}

interface ExitValidationReviewBasis {
  readonly validationState: AnnotationSetValidationState;
  readonly sourceRevision: AnnotationSetValidationState["sourceRevision"];
  readonly dirtyTargets: readonly Readonly<{
    frameId: string;
    targetId: string;
    target: unknown;
  }>[];
}

function captureExitValidationReviewBasis(
  controller: AnnotationStudioController,
  validationState: AnnotationSetValidationState,
): ExitValidationReviewBasis {
  const draft = controller.getSnapshot().draft;
  return {
    validationState,
    sourceRevision: validationState.sourceRevision,
    dirtyTargets:
      draft?.dirtyScopes.flatMap((scope) =>
        scope.kind === "target"
          ? [
              {
                frameId: scope.frameId,
                targetId: scope.targetId,
                target: draft.frames[scope.frameId]?.targets[scope.targetId],
              },
            ]
          : [],
      ) ?? [],
  };
}

function exitValidationReviewBasisMatches(
  controller: AnnotationStudioController,
  basis: ExitValidationReviewBasis | null,
): boolean {
  if (!basis) return false;
  const snapshot = controller.getSnapshot();
  const validationState = snapshot.annotationSetValidation;
  if (
    validationState !== basis.validationState ||
    !Object.is(validationState?.sourceRevision, basis.sourceRevision)
  ) {
    return false;
  }
  const currentDirtyTargets =
    snapshot.draft?.dirtyScopes.flatMap((scope) =>
      scope.kind === "target"
        ? [
            {
              frameId: scope.frameId,
              targetId: scope.targetId,
              target: snapshot.draft?.frames[scope.frameId]?.targets[scope.targetId],
            },
          ]
        : [],
    ) ?? [];
  return (
    currentDirtyTargets.length === basis.dirtyTargets.length &&
    currentDirtyTargets.every((target, index) => {
      const expected = basis.dirtyTargets[index];
      return (
        expected !== undefined &&
        target.frameId === expected.frameId &&
        target.targetId === expected.targetId &&
        target.target === expected.target
      );
    })
  );
}

function waitForAnnotationSetValidationResult(
  controller: AnnotationStudioController,
): Promise<AnnotationSetValidationState | null> {
  const current = controller.getSnapshot().annotationSetValidation ?? null;
  if (current?.status !== "validating") return Promise.resolve(current);

  return new Promise((resolve) => {
    const unsubscribe = controller.subscribe(() => {
      const next = controller.getSnapshot().annotationSetValidation ?? null;
      if (next?.status === "validating") return;
      unsubscribe();
      resolve(next);
    });
  });
}

export function useStudioNavigationGuards({
  controller,
  locale,
  onExit,
  onExitRequestSettled,
  validationReviewRequestId,
  onValidationReviewSettled,
  selectedStreamId,
  availableStreamIds,
  dirtyFrameIdsByStream,
  onChangeStream,
}: {
  controller: AnnotationStudioController;
  locale: AnnotationStudioLocale;
  onExit?: (result: AnnotationStudioExitResult) => void | Promise<void>;
  onExitRequestSettled?: (result: AnnotationStudioExitRequestResult) => void;
  validationReviewRequestId?: number;
  onValidationReviewSettled?: (result: AnnotationStudioValidationReviewResult) => void;
  selectedStreamId: string | null;
  availableStreamIds: ReadonlySet<string>;
  dirtyFrameIdsByStream: ReadonlyMap<string, readonly string[]>;
  onChangeStream: (streamId: string) => void;
}) {
  const [exitActionState, setExitActionState] = useState<StudioExitActionState>("idle");
  const exitActionPendingRef = useRef(false);
  const [exitValidationReviewRequested, setExitValidationReviewRequested] = useState(false);
  const exitValidationReviewBasisRef = useRef<ExitValidationReviewBasis | null>(null);
  const activeExitRequestIdRef = useRef<number | null>(null);
  const exitRequestSettledRef = useRef(onExitRequestSettled);
  exitRequestSettledRef.current = onExitRequestSettled;
  const exitRequestMountedRef = useRef(true);
  const exitRequestUnmountTimerRef = useRef<number | null>(null);
  const [pendingValidationReviewRequest, setPendingValidationReviewRequest] =
    useState<PendingValidationReviewRequest | null>(null);
  const validationReviewSerialRef = useRef(0);
  const activeValidationReviewRequestRef = useRef<PendingValidationReviewRequest | null>(null);
  const previousValidationReviewRequestIdRef = useRef(validationReviewRequestId);
  const validationReviewSettledRef = useRef(onValidationReviewSettled);
  validationReviewSettledRef.current = onValidationReviewSettled;
  const validationReviewMountedRef = useRef(true);
  const validationReviewUnmountTimerRef = useRef<number | null>(null);
  const [exitError, setExitError] = useState<string | null>(null);
  const [pendingStreamChange, setPendingStreamChange] = useState<PendingStreamChange | null>(null);
  const [streamChangeSaving, setStreamChangeSaving] = useState(false);
  const [streamChangeError, setStreamChangeError] = useState<string | null>(null);
  const [currentStreamSavePending, setCurrentStreamSavePending] = useState(false);
  const currentStreamSavePendingRef = useRef(false);
  const [currentStreamSaveError, setCurrentStreamSaveError] = useState<string | null>(null);

  const settleExitRequest = useCallback((approved: boolean, validationOverridden: boolean) => {
    const requestId = activeExitRequestIdRef.current;
    if (requestId === null) return;
    activeExitRequestIdRef.current = null;
    exitRequestSettledRef.current?.({ requestId, approved, validationOverridden });
  }, []);

  useEffect(() => {
    exitRequestMountedRef.current = true;
    if (exitRequestUnmountTimerRef.current !== null) {
      window.clearTimeout(exitRequestUnmountTimerRef.current);
      exitRequestUnmountTimerRef.current = null;
    }
    return () => {
      exitRequestMountedRef.current = false;
      exitRequestUnmountTimerRef.current = window.setTimeout(() => {
        if (exitRequestMountedRef.current) return;
        settleExitRequest(false, false);
      }, 0);
    };
  }, [settleExitRequest]);

  const settleValidationReview = useCallback(
    (
      request: PendingValidationReviewRequest,
      result: Omit<AnnotationStudioValidationReviewResult, "requestId">,
    ) => {
      if (activeValidationReviewRequestRef.current?.serial !== request.serial) return;
      activeValidationReviewRequestRef.current = null;
      if (validationReviewMountedRef.current) setPendingValidationReviewRequest(null);
      validationReviewSettledRef.current?.({ requestId: request.requestId, ...result });
    },
    [],
  );

  const cancelValidationReview = useCallback(() => {
    const request = activeValidationReviewRequestRef.current;
    if (!request) return;
    settleValidationReview(request, { approved: false, validationOverridden: false });
  }, [settleValidationReview]);

  const approveValidationReviewOverride = useCallback(() => {
    const request = activeValidationReviewRequestRef.current;
    if (!request) return;
    settleValidationReview(request, { approved: true, validationOverridden: true });
  }, [settleValidationReview]);

  useEffect(() => {
    validationReviewMountedRef.current = true;
    if (validationReviewUnmountTimerRef.current !== null) {
      window.clearTimeout(validationReviewUnmountTimerRef.current);
      validationReviewUnmountTimerRef.current = null;
    }
    return () => {
      validationReviewMountedRef.current = false;
      validationReviewUnmountTimerRef.current = window.setTimeout(() => {
        if (validationReviewMountedRef.current) return;
        const request = activeValidationReviewRequestRef.current;
        if (!request) return;
        activeValidationReviewRequestRef.current = null;
        validationReviewSettledRef.current?.({
          requestId: request.requestId,
          approved: false,
          validationOverridden: false,
        });
      }, 0);
    };
  }, []);

  useEffect(() => {
    const previousRequestId = previousValidationReviewRequestIdRef.current;
    previousValidationReviewRequestIdRef.current = validationReviewRequestId;
    if (
      validationReviewRequestId === undefined ||
      validationReviewRequestId === previousRequestId ||
      onValidationReviewSettled === undefined
    ) {
      return;
    }

    const supersededRequest = activeValidationReviewRequestRef.current;
    if (supersededRequest) {
      settleValidationReview(supersededRequest, {
        approved: false,
        validationOverridden: false,
      });
    }

    const request = {
      requestId: validationReviewRequestId,
      serial: ++validationReviewSerialRef.current,
    };
    activeValidationReviewRequestRef.current = request;

    const review = async () => {
      const initialSnapshot = controller.getSnapshot();
      if (
        controller.validateAnnotationSet == null ||
        initialSnapshot.annotationSetValidation == null ||
        ((initialSnapshot.draft?.orderedFrameIds.length ?? 0) === 0 &&
          (initialSnapshot.savedFrameIndex?.orderedFrames.length ?? 0) === 0)
      ) {
        settleValidationReview(request, {
          approved: true,
          validationOverridden: false,
        });
        return;
      }

      try {
        await controller.validateAnnotationSet();
        if (activeValidationReviewRequestRef.current?.serial !== request.serial) return;
        let result = controller.getSnapshot().annotationSetValidation ?? null;
        if (result?.status === "validating") {
          result = await waitForAnnotationSetValidationResult(controller);
        }
        if (activeValidationReviewRequestRef.current?.serial !== request.serial) return;
        if (result?.status === "ready") {
          settleValidationReview(request, {
            approved: true,
            validationOverridden: false,
          });
          return;
        }
      } catch {
        // A failed validation is reviewed like the controller's normalized error state.
      }

      if (
        validationReviewMountedRef.current &&
        activeValidationReviewRequestRef.current?.serial === request.serial
      ) {
        setPendingValidationReviewRequest(request);
      }
    };

    void review();
  }, [controller, onValidationReviewSettled, settleValidationReview, validationReviewRequestId]);

  useEffect(() => {
    setCurrentStreamSaveError(null);
  }, [selectedStreamId]);

  const saveDirtyAnnotationFrames = async (
    frameIds: readonly string[],
    validateAfterEach = true,
  ): Promise<boolean> => {
    for (const frameId of frameIds) {
      const currentDraft = controller.getSnapshot().draft;
      const frameIsDirty = currentDraft?.dirtyScopes.some(
        (scope) => scope.kind === "target" && scope.frameId === frameId,
      );
      if (!frameIsDirty) continue;
      await controller.saveAnnotationFrame(frameId, { validateAfterSave: validateAfterEach });
      const latestDraft = controller.getSnapshot().draft;
      const frameStillDirty = latestDraft?.dirtyScopes.some(
        (scope) => scope.kind === "target" && scope.frameId === frameId,
      );
      if (frameStillDirty) return false;
    }
    return true;
  };

  const exitStudio = async ({
    skipValidation = false,
    requestId,
  }: { skipValidation?: boolean; requestId?: number } = {}) => {
    if (requestId !== undefined) {
      if (activeExitRequestIdRef.current !== null && activeExitRequestIdRef.current !== requestId) {
        settleExitRequest(false, false);
      }
      activeExitRequestIdRef.current = requestId;
    }
    if (!onExit || exitActionPendingRef.current) {
      if (requestId !== undefined) settleExitRequest(false, false);
      return;
    }
    exitActionPendingRef.current = true;

    const initialSnapshot = controller.getSnapshot();
    // "Continue anyway" only approves the exact validation result shown in the modal. A
    // canonical revision or dirty-target change makes that approval stale, so the same click
    // reruns preflight instead of bypassing the newer source.
    const validationOverridden =
      skipValidation &&
      exitValidationReviewBasisMatches(controller, exitValidationReviewBasisRef.current);
    const dirtyAnnotationFrameIds =
      initialSnapshot.draft?.orderedFrameIds.filter((frameId) =>
        initialSnapshot.draft?.dirtyScopes.some(
          (scope) => scope.kind === "target" && scope.frameId === frameId,
        ),
      ) ?? [];
    const hasCapturedFrames =
      (initialSnapshot.draft?.orderedFrameIds.length ?? 0) > 0 ||
      (initialSnapshot.savedFrameIndex?.orderedFrames.length ?? 0) > 0;
    const saveThenValidate = initialSnapshot.exitPreflightOrder === "save-then-validate";
    const validationPreflightApplies =
      !validationOverridden &&
      initialSnapshot.annotationSetValidation != null &&
      controller.validateAnnotationSet != null &&
      hasCapturedFrames;

    setExitError(null);
    setExitActionState(
      validationPreflightApplies && !saveThenValidate
        ? "checking"
        : dirtyAnnotationFrameIds.length > 0
          ? "saving"
          : "leaving",
    );
    try {
      if (dirtyAnnotationFrameIds.length > 0 && saveThenValidate) {
        setExitActionState("saving");
        if (!(await saveDirtyAnnotationFrames(dirtyAnnotationFrameIds, false))) {
          setExitError(
            resolveAnnotationOperationErrorMessage(
              controller.getSnapshot().operations.save.error,
              locale,
            ) ?? annotationStudioText("operationFailed", locale),
          );
          settleExitRequest(false, false);
          return;
        }
      }

      if (validationPreflightApplies) {
        if (saveThenValidate) setExitActionState("checking");
        let validationResult = controller.getSnapshot().annotationSetValidation ?? null;
        if (
          saveThenValidate ||
          validationResult?.status === "idle" ||
          validationResult?.status === "stale"
        ) {
          await controller.validateAnnotationSet?.();
          validationResult = controller.getSnapshot().annotationSetValidation ?? null;
        }
        if (validationResult?.status === "validating") {
          validationResult = await waitForAnnotationSetValidationResult(controller);
        }
        if (validationResult?.status !== "ready") {
          if (validationResult) {
            exitValidationReviewBasisRef.current = captureExitValidationReviewBasis(
              controller,
              validationResult,
            );
          }
          setExitValidationReviewRequested(true);
          return;
        }
      }

      if (dirtyAnnotationFrameIds.length > 0 && !saveThenValidate) setExitActionState("saving");
      if (!saveThenValidate && !(await saveDirtyAnnotationFrames(dirtyAnnotationFrameIds))) {
        setExitError(
          resolveAnnotationOperationErrorMessage(
            controller.getSnapshot().operations.save.error,
            locale,
          ) ?? annotationStudioText("operationFailed", locale),
        );
        settleExitRequest(false, false);
        return;
      }

      setExitActionState("leaving");
      const remainingDirtyScopes = controller.getSnapshot().draft?.dirtyScopes ?? [];
      if (remainingDirtyScopes.some((scope) => scope.kind === "target")) {
        settleExitRequest(false, false);
        return;
      }
      const hasRemainingGuardedChanges = remainingDirtyScopes.some((scope) => scope.kind === "set");
      if (hasRemainingGuardedChanges && !(await controller.requestLeave("close"))) {
        settleExitRequest(false, false);
        return;
      }
      await onExit({ validationOverridden });
      exitValidationReviewBasisRef.current = null;
      setExitValidationReviewRequested(false);
      settleExitRequest(true, validationOverridden);
    } catch {
      setExitError(annotationStudioText("exitStudioFailed", locale));
      settleExitRequest(false, false);
    } finally {
      exitActionPendingRef.current = false;
      setExitActionState("idle");
    }
  };

  const requestStreamChange = (streamId: string, afterChange?: () => void) => {
    if (currentStreamSavePendingRef.current) return;
    if (streamId === selectedStreamId) {
      afterChange?.();
      return;
    }
    const currentStreamDirtyFrameIds = selectedStreamId
      ? (dirtyFrameIdsByStream.get(selectedStreamId) ?? [])
      : [];
    if (currentStreamDirtyFrameIds.length > 0) {
      setStreamChangeError(null);
      setPendingStreamChange({ streamId, dirtyFrameIds: currentStreamDirtyFrameIds, afterChange });
      return;
    }
    onChangeStream(streamId);
    afterChange?.();
  };

  const discardAndChangeStream = () => {
    if (!pendingStreamChange || streamChangeSaving) return;
    const { streamId, dirtyFrameIds, afterChange } = pendingStreamChange;
    if (streamId === controller.getSnapshot().selectedStreamId) {
      setPendingStreamChange(null);
      afterChange?.();
      return;
    }
    if (!availableStreamIds.has(streamId)) {
      setPendingStreamChange(null);
      return;
    }
    for (const dirtyFrameId of dirtyFrameIds) {
      controller.discardFrameChanges(dirtyFrameId);
    }
    setStreamChangeError(null);
    setPendingStreamChange(null);
    onChangeStream(streamId);
    afterChange?.();
  };

  const saveAndChangeStream = async () => {
    if (!pendingStreamChange || streamChangeSaving) return;
    const { streamId, dirtyFrameIds, afterChange } = pendingStreamChange;
    const currentSnapshot = controller.getSnapshot();
    if (streamId === currentSnapshot.selectedStreamId) {
      setPendingStreamChange(null);
      afterChange?.();
      return;
    }
    if (!availableStreamIds.has(streamId)) {
      setPendingStreamChange(null);
      return;
    }
    const pendingFrameIdSet = new Set(dirtyFrameIds);
    const orderedDirtyFrameIds =
      currentSnapshot.draft?.orderedFrameIds.filter(
        (frameId) =>
          pendingFrameIdSet.has(frameId) &&
          currentSnapshot.draft?.frames[frameId]?.streamId === currentSnapshot.selectedStreamId,
      ) ?? [];

    setStreamChangeSaving(true);
    setStreamChangeError(null);
    try {
      if (!(await saveDirtyAnnotationFrames(orderedDirtyFrameIds))) {
        setStreamChangeError(
          resolveAnnotationOperationErrorMessage(
            controller.getSnapshot().operations.save.error,
            locale,
          ) ?? annotationStudioText("operationFailed", locale),
        );
        return;
      }
      setPendingStreamChange(null);
      onChangeStream(streamId);
      afterChange?.();
    } catch {
      setStreamChangeError(
        resolveAnnotationOperationErrorMessage(
          controller.getSnapshot().operations.save.error,
          locale,
        ) ?? annotationStudioText("operationFailed", locale),
      );
    } finally {
      setStreamChangeSaving(false);
    }
  };

  const closeStreamChange = () => {
    if (streamChangeSaving) return;
    setStreamChangeError(null);
    setPendingStreamChange(null);
  };

  const saveCurrentStream = async () => {
    if (
      !selectedStreamId ||
      currentStreamSavePendingRef.current ||
      streamChangeSaving ||
      exitActionPendingRef.current
    ) {
      return;
    }

    const currentSnapshot = controller.getSnapshot();
    const streamId = currentSnapshot.selectedStreamId;
    if (
      streamId !== selectedStreamId ||
      currentSnapshot.operations.capture.pending ||
      currentSnapshot.operations.segment.pending ||
      currentSnapshot.operations.save.pending ||
      currentSnapshot.operations.remove.pending
    ) {
      return;
    }
    const requestedFrameIds = new Set(dirtyFrameIdsByStream.get(streamId) ?? []);
    const orderedDirtyFrameIds =
      currentSnapshot.draft?.orderedFrameIds.filter(
        (frameId) =>
          requestedFrameIds.has(frameId) &&
          currentSnapshot.draft?.frames[frameId]?.streamId === streamId,
      ) ?? [];
    if (orderedDirtyFrameIds.length === 0) return;

    currentStreamSavePendingRef.current = true;
    setCurrentStreamSavePending(true);
    setCurrentStreamSaveError(null);
    try {
      if (!(await saveDirtyAnnotationFrames(orderedDirtyFrameIds))) {
        setCurrentStreamSaveError(
          resolveAnnotationOperationErrorMessage(
            controller.getSnapshot().operations.save.error,
            locale,
          ) ?? annotationStudioText("operationFailed", locale),
        );
      }
    } catch {
      setCurrentStreamSaveError(
        resolveAnnotationOperationErrorMessage(
          controller.getSnapshot().operations.save.error,
          locale,
        ) ?? annotationStudioText("operationFailed", locale),
      );
    } finally {
      currentStreamSavePendingRef.current = false;
      setCurrentStreamSavePending(false);
    }
  };

  return {
    exitActionState,
    exitValidationReviewRequested,
    exitError,
    cancelExitValidationReview: () => {
      exitValidationReviewBasisRef.current = null;
      setExitValidationReviewRequested(false);
      settleExitRequest(false, false);
    },
    pendingValidationReviewRequest,
    cancelValidationReview,
    approveValidationReviewOverride,
    exitStudio,
    pendingStreamChange,
    streamChangeSaving,
    streamChangeError,
    requestStreamChange,
    closeStreamChange,
    discardAndChangeStream,
    saveAndChangeStream,
    currentStreamSavePending,
    currentStreamSaveError,
    saveCurrentStream,
  };
}
